import * as moment from 'moment';
import { exportToExcel } from 'src/app/utils/utils.common';
import {
  breakdownRowName,
  cooperateDisplayName,
  driverDisplayName,
  fleetDisplayName,
  fleetVin,
  fmtMiles,
  millisToHours,
  parseDriverRevenueKpis,
  parseRevenueKpis,
  sumField,
  timelineStatusLabel
} from './kpi-helpers';
import {
  aggregateTimelineStatus,
  cancellationBreakdownSegments,
  driverDigestSegments,
  driverDigestStatItems,
  mapBreakdownRows,
  mapCooperateRows,
  formatSharePercent,
  mapCooperateStackedRows,
  mapDriverMilesStackedRows,
  mapDriverRows,
  mapDriverTimelinessStackedRows,
  mapOverviewBreakdownItems,
  mapOverviewFundingItems,
  overviewStatItems,
  revenueStatItems,
  revenueStatusSegments,
  timelinessPercentSegments,
  timelinessSegments,
  timelinessStatItems,
  topDriversByHours,
  tripStatusSegments,
  tripsStatItems
} from './chart-helpers';
import { getTabLabel, TAB_IDS } from './tabs';

export interface BusinessReportExportContext {
  tabId: string;
  data: any;
  startDate: string;
  endDate: string;
  timeZone?: string;
  scoringMode?: 'merged' | 'pickup' | 'dropoff';
  /** @deprecated Prefer scoringMode */
  isDropOffTime?: boolean;
}

function scoringModeLabel(mode: 'merged' | 'pickup' | 'dropoff'): string {
  if (mode === 'dropoff') return 'Drop-off Time';
  if (mode === 'pickup') return 'Pickup Time';
  return 'Pickup + Drop-off';
}

function resolveScoringMode(context: BusinessReportExportContext): 'merged' | 'pickup' | 'dropoff' {
  if (context.scoringMode === 'pickup' || context.scoringMode === 'dropoff' || context.scoringMode === 'merged') {
    return context.scoringMode;
  }
  if (context.isDropOffTime === true) return 'dropoff';
  if (context.isDropOffTime === false) return 'pickup';
  return 'merged';
}

function dateRangeLabel(startDate: string, endDate: string, timeZone?: string): string {
  const format = 'MM-DD-YYYY';
  if (timeZone) {
    return `${moment(startDate).tz(timeZone).format(format)}_${moment(endDate).tz(timeZone).format(format)}`;
  }
  return `${moment(startDate).format(format)}_${moment(endDate).format(format)}`;
}

function statSheet(items: Array<{ label: string; value: any }>): any[][] {
  return [['Metric', 'Value'], ...(items || []).map((item) => [item.label, item.value != null ? item.value : 0])];
}

function tableSheet(headers: string[], rows: any[][]): any[][] {
  return [headers, ...rows];
}

function formatDateTime(value: any): string {
  if (!value) return '';
  return new Date(value).toLocaleString();
}

function sumItemValues(items: Array<{ value?: number }>): number {
  return (items || []).reduce((sum, item) => sum + (Number(item.value) || 0), 0);
}

function percentOfTotal(value: number, total: number): string {
  return formatSharePercent(value, total);
}

/** Mirrors bar/donut chart table view: Category, Value, optional % of Total. */
function chartTableSheet(
  items: Array<{ label: string; value: number }>,
  options: { valueHeader?: string; includePercent?: boolean } = {}
): any[][] {
  const valueHeader = options.valueHeader || 'Value';
  const includePercent = options.includePercent !== false;
  const total = sumItemValues(items);
  const headers = includePercent ? ['Category', valueHeader, '% of Total'] : ['Category', valueHeader];
  const rows = (items || []).map((item) => {
    const value = item.value != null ? item.value : 0;
    const row = [item.label, value];
    if (includePercent) {
      row.push(percentOfTotal(Number(value), total));
    }
    return row;
  });
  return tableSheet(headers, rows);
}

function sortByValueDesc(items: Array<{ label: string; value: number }>): Array<{ label: string; value: number }> {
  return [...(items || [])].sort((a, b) => (Number(b.value) || 0) - (Number(a.value) || 0));
}

/** Mirrors stacked-bar chart table view. */
function stackedTableSheet(items: Array<{ label: string; segments?: Array<{ label: string; value: number }> }>): any[][] {
  const segmentLabels: string[] = [];
  (items || []).forEach((item) => {
    (item.segments || []).forEach((segment) => {
      if (segmentLabels.indexOf(segment.label) === -1) {
        segmentLabels.push(segment.label);
      }
    });
  });

  const headers = ['Name', ...segmentLabels, 'Total'];
  const rows = (items || []).map((item) => {
    const total = (item.segments || []).reduce((sum, segment) => sum + (Number(segment.value) || 0), 0);
    const row: any[] = [item.label];
    segmentLabels.forEach((segLabel) => {
      const segment = (item.segments || []).find((s) => s.label === segLabel);
      row.push(segment ? Number(segment.value) || 0 : 0);
    });
    row.push(total);
    return row;
  });

  return tableSheet(headers, rows);
}

function exportOverview(data: any): Record<string, any[][]> {
  const kpiCards = data.kpiCards || {};
  const charts = data.charts || {};

  const fundingItems = mapOverviewFundingItems(charts.fundingSource);
  const spaceItems = mapOverviewBreakdownItems(charts.spaceType, 'spaceType');
  const losItems = mapOverviewBreakdownItems(charts.los, 'los');
  const timelinessItems = timelinessPercentSegments(charts.timeliness);
  const completionSegments = tripStatusSegments(
    kpiCards.totalTrips,
    kpiCards.completedTrips,
    kpiCards.cancelledTrips
  );

  return {
    Summary: statSheet(overviewStatItems(kpiCards)),
    'Trip Completion': chartTableSheet(completionSegments),
    Timeliness: chartTableSheet(timelinessItems, { valueHeader: 'Percent', includePercent: false }),
    'Funding Source — Trips': chartTableSheet(fundingItems),
    'Space Type — Trips': chartTableSheet(spaceItems),
    'Level of Service — Trips': chartTableSheet(losItems)
  };
}

function exportTrips(data: any): Record<string, any[][]> {
  const kpiCards = data.kpiCards || {};
  const breakdown = data.cancellationBreakdown || {};
  const cooperateKpis = data.cooperateKpis || [];
  const fleetKpis = data.fleetKpis || [];
  const driverKpis = data.driverKpis || [];

  const totalTrips = kpiCards.totalTrips;
  const totalCancelled = kpiCards.totalCancelled;
  const totalCompleted = kpiCards.totalCompleted;

  const statusSegments = tripStatusSegments(totalTrips, totalCompleted, totalCancelled);
  const cancellationSegments = cancellationBreakdownSegments(breakdown);
  const fundingBarItems = sortByValueDesc(mapCooperateRows(cooperateKpis, 'totalJobs'));
  const driverTripItems = sortByValueDesc(mapDriverRows(driverKpis, 'totalJobs'));
  const driverMilesItems = sortByValueDesc(mapDriverMilesStackedRows(driverKpis));
  const stackedFundingItems = sortByValueDesc(mapCooperateStackedRows(cooperateKpis));

  return {
    Summary: statSheet(tripsStatItems(totalTrips, totalCancelled, totalCompleted)),
    'Trip Status': chartTableSheet(statusSegments),
    'Cancellation Breakdown': chartTableSheet(cancellationSegments),
    'Trips by Funding Source': chartTableSheet(fundingBarItems),
    'Top Drivers by Trips': chartTableSheet(driverTripItems),
    'Top Drivers by Miles': stackedTableSheet(driverMilesItems),
    'Funding Source — Status': stackedTableSheet(stackedFundingItems),
    'Funding Source': tableSheet(
      ['Funding Source', 'Total Jobs', 'Completed', 'Cancelled', 'CAD', 'Miles'],
      cooperateKpis.map((row: any) => [
        cooperateDisplayName(row),
        row.totalJobs || 0,
        row.totalCompletedJobs || 0,
        row.totalCancelledJobs || 0,
        row.totalCadJobs || 0,
        fmtMiles(row.totalMiles)
      ])
    ),
    Fleets: tableSheet(
      ['Fleet', 'VIN', 'Total Jobs', 'Completed', 'Cancelled', 'CAD', 'Miles'],
      fleetKpis.map((row: any) => [
        fleetDisplayName(row),
        fleetVin(row),
        row.totalJobs || 0,
        row.totalCompletedJobs || 0,
        row.totalCancelledJobs || 0,
        row.totalCadJobs || 0,
        fmtMiles(row.totalMiles)
      ])
    ),
    Drivers: tableSheet(
      ['Driver', 'Total Jobs', 'Completed', 'Cancelled', 'Miles'],
      [
        ...driverKpis.map((row: any) => [
          driverDisplayName(row.driver),
          row.totalJobs || 0,
          row.totalCompletedJobs || 0,
          row.totalCancelledJobs || 0,
          fmtMiles(row.totalMiles)
        ]),
        [
          'Total',
          sumField(driverKpis, 'totalJobs'),
          sumField(driverKpis, 'totalCompletedJobs'),
          sumField(driverKpis, 'totalCancelledJobs'),
          fmtMiles(sumField(driverKpis, 'totalMiles'))
        ]
      ]
    )
  };
}

function exportRevenue(data: any): Record<string, any[][]> {
  const { summary, sources: fundingSources } = parseRevenueKpis(data.revenueKpis);
  const { sources: driverSources } = parseDriverRevenueKpis(data.driverRevenueKpis);
  const kpiCards = data.kpiCards || {};
  const cards = {
    total: kpiCards.total != null ? kpiCards.total : summary.total,
    totalCadJobs: kpiCards.totalCadJobs != null ? kpiCards.totalCadJobs : summary.totalCadJobs,
    totalCancelledJobs: kpiCards.totalCancelledJobs != null ? kpiCards.totalCancelledJobs : summary.totalCancelledJobs,
    totalCompletedJobs: kpiCards.totalCompletedJobs != null ? kpiCards.totalCompletedJobs : summary.totalCompletedJobs,
    totalRemainingJobs: kpiCards.totalRemainingJobs != null ? kpiCards.totalRemainingJobs : summary.totalRemainingJobs
  };

  const statusSegments = revenueStatusSegments(cards);
  const fundingRevenueItems = sortByValueDesc(
    fundingSources.map((row: any) => ({ label: cooperateDisplayName(row), value: Number(row.total) || 0 }))
  );
  const driverRevenueItems = sortByValueDesc(
    driverSources.map((row: any) => ({ label: driverDisplayName(row._id), value: Number(row.total) || 0 }))
  );
  const completedRevenueItems = sortByValueDesc(
    fundingSources.map((row: any) => ({
      label: cooperateDisplayName(row),
      value: Number(row.totalCompletedJobs) || 0
    }))
  );

  return {
    Summary: statSheet(revenueStatItems(cards)),
    'Revenue Breakdown': chartTableSheet(statusSegments),
    'Revenue by Funding Source': chartTableSheet(fundingRevenueItems, { valueHeader: 'Revenue' }),
    'Top Drivers by Revenue': chartTableSheet(driverRevenueItems, { valueHeader: 'Revenue' }),
    'Completed Revenue by FS': chartTableSheet(completedRevenueItems, { valueHeader: 'Revenue' }),
    'Funding Source': tableSheet(
      ['Funding Source', 'Revenue', 'Completed', 'Cancelled', 'Remaining'],
      fundingSources.map((row: any) => [
        cooperateDisplayName(row),
        row.total || 0,
        row.totalCompletedJobs || 0,
        row.totalCancelledJobs || 0,
        row.totalRemainingJobs || 0
      ])
    ),
    Drivers: tableSheet(
      ['Driver', 'Revenue', 'Completed', 'Cancel Charge', 'No-show / CAD', 'Remaining'],
      driverSources.map((row: any) => [
        driverDisplayName(row._id),
        row.total || 0,
        row.totalCompletedJobs || 0,
        row.totalCancelChargeJobs || 0,
        row.totalCadJobs || 0,
        row.totalRemainingJobs || 0
      ])
    )
  };
}

function exportBreakdown(data: any): Record<string, any[][]> {
  const breakdownHeaders = ['Name', 'Total Jobs', 'Completed', 'Cancelled', 'CAD', 'Miles', 'Revenue'];
  const mapBreakdownTableRows = (rows: any[], nameField: string) =>
    (rows || []).map((row) => [
      breakdownRowName(row, nameField),
      row.totalJobs || 0,
      row.totalCompletedJobs || 0,
      row.totalCancelledJobs || 0,
      row.totalCadJobs || 0,
      fmtMiles(row.totalMiles),
      row.totalRevenue || 0
    ]);

  const spaceTypeKpis = data.spaceTypeKpis || [];
  const losKpis = data.losKpis || [];
  const serviceAreaKpis = data.serviceAreaKpis || [];
  const totals = data.totals || [];

  const spaceTypeJobs = sortByValueDesc(mapBreakdownRows(spaceTypeKpis, 'spaceType', 'totalJobs'));
  const spaceTypeRevenue = sortByValueDesc(mapBreakdownRows(spaceTypeKpis, 'spaceType', 'totalRevenue'));
  const losJobs = sortByValueDesc(mapBreakdownRows(losKpis, 'los', 'totalJobs'));
  const losRevenue = sortByValueDesc(mapBreakdownRows(losKpis, 'los', 'totalRevenue'));
  const serviceAreaJobs = sortByValueDesc(mapBreakdownRows(serviceAreaKpis, 'serviceArea', 'totalJobs'));
  const serviceAreaRevenue = sortByValueDesc(mapBreakdownRows(serviceAreaKpis, 'serviceArea', 'totalRevenue'));

  return {
    Totals: statSheet(totals),
    'Jobs by Space Type': chartTableSheet(spaceTypeJobs),
    'Space Type — Jobs': chartTableSheet(spaceTypeJobs),
    'Space Type — Revenue': chartTableSheet(spaceTypeRevenue, { valueHeader: 'Revenue' }),
    'Space Type Breakdown': tableSheet(breakdownHeaders, mapBreakdownTableRows(spaceTypeKpis, 'spaceType')),
    'Level of Service — Jobs': chartTableSheet(losJobs),
    'Level of Service — Revenue': chartTableSheet(losRevenue, { valueHeader: 'Revenue' }),
    'Level of Service Breakdown': tableSheet(breakdownHeaders, mapBreakdownTableRows(losKpis, 'los')),
    'Service Area — Jobs': chartTableSheet(serviceAreaJobs),
    'Service Area — Revenue': chartTableSheet(serviceAreaRevenue, { valueHeader: 'Revenue' }),
    'Service Area Breakdown': tableSheet(breakdownHeaders, mapBreakdownTableRows(serviceAreaKpis, 'serviceArea'))
  };
}

function exportTimeliness(data: any, scoringMode: 'merged' | 'pickup' | 'dropoff'): Record<string, any[][]> {
  const kpiCards = data.kpiCards || {};
  const drivers = data.drivers || [];

  const tripSegments = timelinessSegments(drivers);
  const onTimeRateItems = sortByValueDesc(
    drivers.map((row: any) => ({ label: driverDisplayName(row.driver), value: Number(row.onTime) || 0 }))
  );
  const tripVolumeItems = sortByValueDesc(
    drivers.map((row: any) => ({ label: driverDisplayName(row.driver), value: Number(row.total) || 0 }))
  );
  const stackedDriverItems = sortByValueDesc(mapDriverTimelinessStackedRows(drivers));

  return {
    Summary: [
      ['Scoring Mode', scoringModeLabel(scoringMode)],
      ...statSheet(timelinessStatItems(kpiCards)).slice(1)
    ],
    'Trip Timeliness Mix': chartTableSheet(tripSegments),
    'On-Time Rate by Driver': chartTableSheet(onTimeRateItems, { valueHeader: 'On-Time %', includePercent: false }),
    'Trip Volume by Driver': chartTableSheet(tripVolumeItems, { valueHeader: 'Trips' }),
    'Driver Timeliness Detail': stackedTableSheet(stackedDriverItems),
    'Driver Timeliness': tableSheet(
      ['Driver', 'On-Time', 'Early', 'Late', 'On-Time %', 'Completed Trips'],
      drivers.map((row: any) => [
        driverDisplayName(row.driver),
        row.onTimeTrips || 0,
        row.earlyTrips || 0,
        row.lateTrips || 0,
        row.onTime != null ? row.onTime : 0,
        row.total || 0
      ])
    )
  };
}

function exportDriverDigest(data: any): Record<string, any[][]> {
  const summary = data.summary || {};
  const isRange = summary.digestScope === 'date-range';
  const timelines = data.timelines || [];
  const driversManifestWithoutTimeline = data.driversManifestWithoutTimeline || [];

  const statusSegments = driverDigestSegments(summary);
  const timelineStatusItems = aggregateTimelineStatus(timelines);
  const hoursByDriver = topDriversByHours(timelines, timelines.length || 999);
  const noTimesheetItems = driversManifestWithoutTimeline.map((driver: any) => ({
    label: driverDisplayName(driver),
    value: driver.manifest ? driver.manifest.length : 0
  }));

  return {
    Summary: statSheet(driverDigestStatItems(summary, isRange)),
    'Driver Availability': chartTableSheet(statusSegments),
    'Timeline Status': chartTableSheet(timelineStatusItems),
    'Top Drivers by Hours': chartTableSheet(hoursByDriver, { valueHeader: 'Hours', includePercent: false }),
    'Missing Timesheet Trips': chartTableSheet(noTimesheetItems, { valueHeader: 'Scheduled Trips', includePercent: false }),
    Timelines: tableSheet(
      ['Driver', 'Status', 'Check-In', 'Hours', ...(isRange ? [] : ['Week Hours'])],
      timelines.map((row: any) => {
        const hours = row.todayHours != null ? row.todayHours : millisToHours(row.workTimeInMillis);
        const base = [
          driverDisplayName(row.driver),
          timelineStatusLabel(row.status),
          formatDateTime(row.checkin),
          hours != null ? hours : 0
        ];
        if (!isRange) {
          const weekHours = millisToHours(row.totalWeekHours);
          base.push(weekHours != null ? weekHours : 0);
        }
        return base;
      })
    ),
    'No Timesheet': tableSheet(
      ['Driver', 'Phone', 'Scheduled Trips'],
      driversManifestWithoutTimeline.map((row: any) => [
        driverDisplayName(row),
        row.contactNumber || '',
        row.manifest ? row.manifest.length : 0
      ])
    )
  };
}

export function exportBusinessReport(context: BusinessReportExportContext): void {
  const { tabId, data, startDate, endDate, timeZone } = context;
  if (!data) return;

  let sheets: Record<string, any[][]>;
  let filenameSuffix = '';
  const scoringMode = resolveScoringMode(context);

  switch (tabId) {
    case TAB_IDS.OVERVIEW:
      sheets = exportOverview(data);
      break;
    case TAB_IDS.TRIPS:
      sheets = exportTrips(data);
      break;
    case TAB_IDS.REVENUE:
      sheets = exportRevenue(data);
      break;
    case TAB_IDS.BREAKDOWN:
      sheets = exportBreakdown(data);
      break;
    case TAB_IDS.TIMELINESS:
      sheets = exportTimeliness(data, scoringMode);
      filenameSuffix = scoringMode === 'merged' ? '-merged' : scoringMode === 'dropoff' ? '-dropoff' : '-pickup';
      break;
    case TAB_IDS.DRIVER_DIGEST:
      sheets = exportDriverDigest(data);
      break;
    default:
      return;
  }

  const label = getTabLabel(tabId).replace(/\s+/g, '-').toLowerCase();
  const filename = `${label}-report${filenameSuffix}_${dateRangeLabel(startDate, endDate, timeZone)}`;

  exportToExcel(`${filename}.xlsx`, sheets);
}
